import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import { makeGetTask, makeIsDependant } from '../selectors/tasks';

import { updateTask, removeTask, moveTask } from '../actions/tasks';
import { duplicateTask } from '../api/tasks-api';

import { createAlert } from '../services/alert-service';

import Popout from './popout';
import PromptPopout from './prompt-popout';
import SelectMilestonePopout from './select-milestone-popout';
import SelectTaskPopout from './select-task-popout';
import DateElement from './date-element';
import LabelPicker from './label-picker';
import TaskListSelector from './task-list-selector';
import TaskArchiveToggle from './TaskArchiveToggle';

import IconMoreHoriz from 'icons/icon-more-horiz';

class TaskActions extends PureComponent {
    state = {
        visible: false
    }

    handleTriggerClick = () => {
        this.popoutCtrl && this.popoutCtrl.show();
    }

    handleEditTaskNameClick = () => {
        this.props.onEditNameClick && this.props.onEditNameClick();

        this.popoutCtrl.hide();
    }

    handleMilestoneSelect = milestone => {
        updateTask(this.props.id, {
            milestone: milestone.id
        });

        this.popoutCtrl.hide();
    }

    handleMilestoneUntie = () => {
        if (!this.props.task.milestone) {
            return;
        }

        updateTask(this.props.id, {
            milestone: null
        });

        this.popoutCtrl.hide();
    }

    handleTaskSelect = task => {
        updateTask(this.props.id, {
            dependency: task.id
        });

        this.popoutCtrl.hide();
    }

    handleTaskUntie = () => {
        if (!this.props.task.dependency) {
            return;
        }

        updateTask(this.props.id, {
            dependency: null
        });

        this.popoutCtrl.hide();
    }

    handleSetDeadlineClick = () => {
        this.dueDateCtrl.show();
    }

    handleDueDateChange = nextDate => {
        const nextDateFormatted = nextDate && moment(nextDate).format();

        if (this.props.task.due_date === nextDateFormatted) {
            return;
        }

        if (this.props.milestone) {
            if (new Date(this.props.milestone.start) > nextDate) {
                createAlert('default', 'Task tied to milestone cannot end earlier than milestone start date');
                return;
            }

            if (new Date(this.props.milestone.end) < nextDate) {
                createAlert('default', 'Task tied to milestone cannot end after milestone end date');
                return;
            }
        }

        updateTask(this.props.id, {
            due_date: nextDateFormatted
        });

        this.popoutCtrl.hide();
    }

    handleDueDateRemove = () => {
        updateTask(this.props.id, {
            due_date: null
        });

        this.dueDateCtrl && this.dueDateCtrl.hide();
        this.popoutCtrl.hide();
    }

    handleUnarchiveClick = () => {
        updateTask(this.props.id, {
            archived: false
        });

        this.popoutCtrl.hide();
    }

    handleArchiveClick = () => {
        updateTask(this.props.id, {
            archived: true
        });

        this.popoutCtrl.hide();
    }

    handleDeleteClick = () => {
        removeTask(this.props.id);
    }

    handleShow = () => {
        this.setState({
            visible: true
        })
    }

    handleHide = () => {
        this.setState({
            visible: false
        })
    }

    setPopoutCtrl = ctrl => this.popoutCtrl = ctrl;
    setDueDateCtrl = ctrl => this.dueDateCtrl = ctrl;

    handleMoveToProjectClick = () => {
        this.taskMoveToProjectCtrl.show();
    }

    handleMoveToProjectConfirm = (taskGroupId, projectId) => {
        if (projectId && projectId !== this.props.task.project) {
            updateTask(this.props.id, {
                project: projectId,
                labels: [],
                assignees: [],
                milestone: null
            }, true);
        }

        moveTask(this.props.id, {
            type: 'task_group',
            id: taskGroupId
        });

        this.popoutCtrl.hide();
    }

    handleDuplicateClick = () => {
        this.duplicateCtrl.show();
    }

    handleDuplicateConfirm = (taskGroupId, projectId) => {
        duplicateTask(this.props.id, taskGroupId);

        this.popoutCtrl.hide();
    }

    render() {
        const { task } = this.props;

        return (
            <div className={'task-actions-holder' + (this.state.visible ? ' visible' : '')}>
                <span className="task-actions-trigger" onClick={this.handleTriggerClick}><IconMoreHoriz /></span>

                <Popout
                    className="xxsmall"
                    onShow={this.handleShow}
                    onHide={this.handleHide}
                    provideController={this.setPopoutCtrl}
                 >
                    <div className="popout-list task-actions">
                        <div className="item">
                            <div className="item-inner" onClick={this.handleEditTaskNameClick}>
                                Edit task name
                            </div>
                        </div>

                        <div className="item">
                            <SelectMilestonePopout
                                title="TIE TASK TO A MILESTONE"
                                projectId={this.props.projectId}
                                selected={this.props.task.milestone}
                                onSelect={this.handleMilestoneSelect}
                                onRemove={this.handleMilestoneUntie}
                                showCreate={true}
                                removeCopy="Untie Milestone"
                                xAlign="right"
                                defaultXPosition="left"
                            >
                                <div className="item-inner">
                                    Tie task to a Milestone
                                </div>
                            </SelectMilestonePopout>
                        </div>

                        <div className="item">
                            <SelectTaskPopout
                                title="SET TASK DEPENDENCY"
                                excluded={[this.props.id]}
                                projectId={this.props.projectId}
                                selected={this.props.task.dependency}
                                milestone={this.props.task.milestone}
                                onSelect={this.handleTaskSelect}
                                onUntie={this.handleTaskUntie}
                                xAlign="right"
                                defaultXPosition="left"
                            >
                                <div className="item-inner">
                                    Set task dependency
                                </div>
                            </SelectTaskPopout>
                        </div>

                        <div className="item">
                            <div className="item-inner" onClick={this.handleSetDeadlineClick}>
                                Add due date
                            </div>

                            <DateElement
                                selected={task.due_date}
                                title={task.due_date ? 'CHANGE DUE DATE' : 'ADD A DUE DATE'}
                                xAlign="right"
                                defaultXPosition="left"
                                removePopoutTitle="REMOVE DUE DATE"
                                removeBodyText="Are you sure you want to remove the due date from this task?"
                                minDate={new Date()}
                                showTimeSelect={true}
                                showQuickOptions={true}
                                onChange={this.handleDueDateChange}
                                onRemove={this.handleDueDateRemove}
                                provideController={this.setDueDateCtrl}
                            />
                        </div>

                        <div className="item">
                            <LabelPicker
                                defaultYPosition="top"
                                defaultXPosition="left"
                                taskId={this.props.id}
                                projectId={this.props.task.project}
                                taskLabels={this.props.task.labels || []}
                            >
                                <div className="item-inner">
                                    Add tags
                                </div>
                            </LabelPicker>
                        </div>

                        {
                            !this.props.isDependant ? (
                                <div className="item">
                                    <div className="item-inner" onClick={this.handleMoveToProjectClick}>
                                        Move task
                                    </div>

                                    <TaskListSelector
                                        title="MOVE TASK"
                                        taskGroupId={this.props.task.task_group}
                                        projectId={this.props.task.project}
                                        onSuccess={this.handleMoveToProjectConfirm}
                                        provideController={ctrl => this.taskMoveToProjectCtrl = ctrl}
                                    />
                                </div>
                            ) : null
                        }

                        <div className="item">
                            <div className="item-inner" onClick={this.handleDuplicateClick}>
                                Duplicate task
                            </div>

                            <TaskListSelector
                                title="DUPLICATE TASK"
                                taskGroupId={this.props.task.task_group}
                                projectId={this.props.task.project}
                                onSuccess={this.handleDuplicateConfirm}
                                provideController={ctrl => this.duplicateCtrl = ctrl}
                            />
                        </div>

                        <TaskArchiveToggle
                            projectId={this.props.task.project}
                            archived={this.props.task.archived}
                            onArchive={this.handleArchiveClick}
                            onUnarchive={this.handleUnarchiveClick}
                        >
                            <div className="item">
                                <div className="item-inner">
                                    { this.props.task.archived ? 'Unarchive' : 'Archive' }
                                </div>
                            </div>
                        </TaskArchiveToggle>

                        <div className="item">
                            <PromptPopout
                                xAlign="right"
                                defaultXPosition="left"
                                title="DELETE TASK"
                                text="Do you really want to delete this task?"
                                confirmText="Delete"
                                confirmClassName="btn-caution"
                            >
                                <div className="item-inner" onClick={this.handleDeleteClick}>
                                    Delete
                                </div>
                            </PromptPopout>
                        </div>
                    </div>
                </Popout>
            </div>
        )
    }
}
const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const isDependant = makeIsDependant();

    return ({ tasks, milestones }, ownProps) => ({
        task: getTask(tasks.items, ownProps.id),
        isDependant: isDependant(ownProps.id, false, tasks.items, milestones.items),
    });
}

export default connect(makeMapStateToProps)(TaskActions);
